
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ReviewItemTable from '@/components/purchase-order/review/ReviewItemTable.vue';
import { purchaseList } from '@/collections/purchase_list';
import { EventBus } from '@/eventBus';
import type { PurchaseListGroup, PurchaseListItem } from '@/types/purchase-list';
import type { InventoryByVariantId } from '@/utils/purchase-order/fetchInventoryByVariantIds';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { groupTotal as computeGroupTotal, type ReviewLineEdits, type SeededTax } from '@/utils/purchase-order/reviewLineEdits';

interface LocationOption { id: number; name: string; }

@Component({ components: { ReviewItemTable } })
export default class VendorReviewCard extends Vue {
  @Prop({ required: true }) public readonly group!: PurchaseListGroup;
  @Prop({ default: () => [] }) public readonly locations!: LocationOption[];
  @Prop({ default: false }) public readonly creating!: boolean;
  @Prop({ default: () => ({}) }) public readonly inventoryByVariantId!: InventoryByVariantId;
  @Prop({ default: false }) public readonly inventoryLoading!: boolean;
  @Prop({ default: () => ({}) }) public readonly edits!: ReviewLineEdits;
  @Prop({ default: null }) public readonly seededTax!: SeededTax | null;
  /** Supplier-lead-time-derived default (today + leadTime days), '' if none.
   *  Resolved by the page after mount, so it may arrive after this card. */
  @Prop({ default: '' }) public readonly defaultExpectedDelivery!: string;

  public locationId: number | null = null;
  public expectedDeliveryDate = '';

  public created(): void {
    // Seed from the default when present (synchronous case). Never overwrite a
    // value the user has already typed.
    if (!this.expectedDeliveryDate && this.defaultExpectedDelivery) {
      this.expectedDeliveryDate = this.defaultExpectedDelivery;
    }
  }

  @Watch('defaultExpectedDelivery')
  public onDefaultExpectedDeliveryChange(value: string): void {
    // The page resolves the default after mount (it fetches the supplier), so
    // the prop can arrive late — seed then too, still guarding a typed value.
    if (!this.expectedDeliveryDate && value) {
      this.expectedDeliveryDate = value;
    }
  }

  public get itemCountLabel(): string {
    const n = this.group.items.length;
    return `${n} item${n === 1 ? '' : 's'}`;
  }

  public get hasActiveItems(): boolean {
    return this.group.items.some((i) => Number(i.quantity) > 0);
  }

  public get canCreate(): boolean {
    return !!this.locationId && this.hasActiveItems;
  }

  public get disabledReason(): string {
    if (this.creating) { return ''; }
    if (!this.hasActiveItems) { return 'Enter a quantity to enable Create PO'; }
    if (!this.locationId) { return 'Pick a delivery location'; }
    return '';
  }

  public get groupTotal(): number {
    return computeGroupTotal(
      this.group.items,
      this.edits,
      this.seededTax ?? undefined,
      this.group.currencyCode || 'USD',
    );
  }

  public get formattedTotal(): string {
    return CurrencyHelper.formatCurrencyWithCode(this.groupTotal, this.group.currencyCode || 'USD');
  }

  public async onQtyChange(payload: { item: PurchaseListItem; quantity: number }): Promise<void> {
    // Optimistic: mutate the reactive item so lineTotal + groupTotal
    // recompute instantly; the API call + page-level refetch follow.
    const previous = Number(payload.item.quantity);
    payload.item.quantity = payload.quantity;
    try {
      await purchaseList.updateItem(payload.item.id, { quantity: payload.quantity });
    } catch {
      payload.item.quantity = previous;
      EventBus.$emit('show-snackbar', {
        message: 'Failed to update quantity',
        color: 'error',
      });
    }
  }

  public async onRemove(item: PurchaseListItem): Promise<void> {
    const idx = this.group.items.findIndex((i) => i.id === item.id);
    if (idx !== -1) { this.group.items.splice(idx, 1); }
    await purchaseList.removeItem(item.id);
  }

  public onCreate(): void {
    if (!this.canCreate || this.creating) { return; }
    this.$emit('create', {
      group: this.group,
      locationId: this.locationId,
      expectedDeliveryDate: this.expectedDeliveryDate,
    });
  }

  public onCancel(): void {
    this.locationId = null;
    // Re-seed to the supplier default (not blank) so cancel restores the
    // pre-filled state rather than clearing it.
    this.expectedDeliveryDate = this.defaultExpectedDelivery || '';
    this.$emit('cancel');
  }
}
